import React, { useState, useEffect } from 'react';
import { X, Clock, Calendar, CheckSquare, Activity, Trash2, Edit2, Save } from 'lucide-react';
import api from '../lib/api';

const getToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const getNow = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

const ClientProfileModal = ({ client, onClose, onUpdate }) => {
  const [sessionForm, setSessionForm] = useState({ date: getToday(), time: getNow(), minutes: 8, cabin: '' });
  const [voucherForm, setVoucherForm] = useState({ minutes: 80, purchase_date: getToday() });
  const [history, setHistory] = useState({ sessions: [], vouchers: [] });
  const [cabins, setCabins] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [sessionPage, setSessionPage] = useState(1);
  const [voucherPage, setVoucherPage] = useState(1);
  const sessionsPerPage = 6;

  useEffect(() => {
    if (client) {
      fetchHistory();
      fetchCabins();
      setEditForm({
        name: client.name || '',
        dni: client.dni || '',
        phone: client.phone || '',
        email: client.email || '',
        date_of_birth: client.date_of_birth || '',
        skin_type: client.skin_type || '',
        rgpd_status: client.rgpd_status || 'Pendiente',
        notes: client.notes || '',
        ads_consent: client.ads_consent ?? false,
        signature: client.signature || '',
        remaining_minutes: client.remaining_minutes || 0
      });
    }
  }, [client]);

  const fetchHistory = async () => {
    try {
      setLoadingHistory(true);
      const res = await api.get(`/clients/${client.id}/history`);
      const data = res.data;
      if (data.sessions) {
        data.sessions.sort((a, b) => {
          const dateB = new Date(`${b.date}T${b.time}`);
          const dateA = new Date(`${a.date}T${a.time}`);
          return dateB - dateA;
        });
      }
      if (data.vouchers) {
        data.vouchers.sort((a, b) => {
          const dateB = new Date(b.purchase_date);
          const dateA = new Date(a.purchase_date);
          return dateB - dateA;
        });
      }
      setHistory(data);
    } catch (e) {
      console.error("Error fetching history", e);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchCabins = async () => {
    try {
      const res = await api.get('/cabins');
      if (res.data && res.data.length > 0) {
        setCabins(res.data);
      } else {
        // Retry once after 1 second if empty
        setTimeout(async () => {
          const retryRes = await api.get('/cabins');
          if (retryRes.data) setCabins(retryRes.data);
        }, 1000);
      }
    } catch (e) {
      console.error("Error fetching cabins", e);
    }
  };

  if (!client) return null;

  const handleCreateSession = async (e) => {
    e.preventDefault();
    if (!sessionForm.date || !sessionForm.time || !sessionForm.minutes || !sessionForm.cabin) {
      alert("Por favor, rellena todos los campos obligatorios.");
      return;
    }
    try {
      // Optimistic update for immediate feedback
      const originalMinutes = client.remaining_minutes;
      client.remaining_minutes -= sessionForm.minutes;

      api.post('/sessions', { ...sessionForm, client_id: client.id }).then(() => {
        if (onUpdate) onUpdate();
        fetchHistory();
      }).catch(err => {
        // Rollback on error
        client.remaining_minutes = originalMinutes;
        alert("Error al crear sesión. Por favor, inténtelo de nuevo.");
      });

      setSessionForm({ date: getToday(), time: getNow(), minutes: 8, cabin: '' });
    } catch (error) {
      console.error("Session creation error", error);
    }
  };

  const handleCreateVoucher = async (e) => {
    e.preventDefault();
    try {
      await api.post('/vouchers', { ...voucherForm, client_id: client.id });
      setVoucherForm({ minutes: 80, purchase_date: getToday() });
      if (onUpdate) onUpdate(); // Refresh parent to get new minutes
      fetchHistory();
    } catch (error) {
      alert("Error al vender el bono.");
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm("¿Seguro que deseas eliminar esta sesión? Los minutos se le devolverán al cliente.")) return;
    try {
      await api.delete(`/sessions/${sessionId}`);
      if (onUpdate) onUpdate();
      fetchHistory();
    } catch (e) {
      alert("Error al eliminar sesión.");
    }
  };

  const handleDeleteVoucher = async (voucherId) => {
    if (!window.confirm("¿Seguro que deseas eliminar este bono? Los minutos se le restarán al cliente.")) return;
    try {
      await api.delete(`/vouchers/${voucherId}`);
      if (onUpdate) onUpdate();
      fetchHistory();
    } catch (e) {
      alert("Error al eliminar bono.");
    }
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/clients/${client.id}`, editForm);
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error updating client", error);
      alert("Error al guardar los cambios del cliente.");
    }
  };

  return (
    <div className="fixed inset-0 bg-stone-900/50 z-[60] flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl overflow-hidden shadow-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 md:p-6 border-b border-stone-100 flex flex-col sm:flex-row items-start justify-between bg-stone-50 gap-4">
          <div className="flex-1 w-full">
            {isEditing ? (
              <div className="space-y-3">
                <input 
                  type="text" 
                  value={editForm.name} 
                  onChange={e => setEditForm({...editForm, name: e.target.value})} 
                  className="text-2xl font-bold text-stone-800 bg-white border border-stone-200 rounded-lg px-3 py-1 w-full focus:ring-2 focus:ring-amber-500 outline-none"
                  placeholder="Nombre del cliente"
                />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-stone-500 font-medium">DNI</label>
                    <input type="text" value={editForm.dni} onChange={e => setEditForm({...editForm, dni: e.target.value})} className="w-full bg-white border border-stone-200 rounded-md px-2 py-1 text-sm outline-none focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 font-medium">Teléfono</label>
                    <input type="text" value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} className="w-full bg-white border border-stone-200 rounded-md px-2 py-1 text-sm outline-none focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 font-medium">Email</label>
                    <input type="email" value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} className="w-full bg-white border border-stone-200 rounded-md px-2 py-1 text-sm outline-none focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 font-medium">Fecha Nacimiento</label>
                    <input type="date" value={editForm.date_of_birth} onChange={e => setEditForm({...editForm, date_of_birth: e.target.value})} className="w-full bg-white border border-stone-200 rounded-md px-2 py-1 text-sm outline-none focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 font-medium">Fototipo</label>
                    <select value={editForm.skin_type} onChange={e => setEditForm({...editForm, skin_type: e.target.value})} className="w-full bg-white border border-stone-200 rounded-md px-2 py-1 text-sm outline-none focus:border-amber-500">
                      <option value="">Seleccionar...</option>
                      <option value="Celta">Celta</option>
                      <option value="Europeo Normal">Europeo Normal</option>
                      <option value="Mediterráneo">Mediterráneo</option>
                      <option value="Asiático">Asiático</option>
                      <option value="Oscuro">Oscuro</option>
                    </select>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-bold text-stone-800">{client.name}</h2>
                  <button onClick={() => setIsEditing(true)} className="p-1.5 text-stone-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="Editar cliente">
                    <Edit2 size={16} />
                  </button>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-stone-500 text-sm mt-2 items-center">
                  <span className="font-medium">{client.dni || 'Sin DNI'}</span>
                  <span>•</span>
                  <span>{client.phone || 'Sin Teléfono'}</span>
                  <span>•</span>
                  <span>{client.email || 'Sin Email'}</span>
                  {client.date_of_birth && (
                    <>
                      <span>•</span>
                      <span>Nac.: {client.date_of_birth.split('-').reverse().join('/')}</span>
                    </>
                  )}
                  {client.skin_type && (
                    <>
                      <span>•</span>
                      <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-md text-xs font-bold border border-blue-100">
                        {client.skin_type}
                      </span>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto">
            <div className="text-left sm:text-center">
              <div className="text-[10px] font-semibold text-stone-400 uppercase tracking-widest mb-0.5">Saldo Actual</div>
              {isEditing ? (
                <div className="flex items-center">
                  <input
                    type="number"
                    value={editForm.remaining_minutes}
                    onChange={e => setEditForm({...editForm, remaining_minutes: parseInt(e.target.value) || 0})}
                    className="w-24 text-2xl sm:text-3xl font-black text-amber-600 bg-white border border-stone-200 rounded-lg px-2 py-1 focus:ring-2 focus:ring-amber-500 outline-none text-center"
                  />
                  <span className="ml-2 font-bold text-amber-600">min</span>
                </div>
              ) : (
                <div className="text-2xl sm:text-3xl font-black text-amber-600">{client.remaining_minutes} min</div>
              )}
            </div>
            <button onClick={onClose} className="p-2 bg-white rounded-full text-stone-400 hover:text-stone-600 shadow-sm border border-stone-200 ml-auto">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            {/* Left Col - Client Info */}
            <div className="space-y-6">
              <div>
                <h3 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
                  <CheckSquare size={18} className="text-stone-400" />
                  Estado Legal
                </h3>
                <div className="bg-stone-50 p-4 rounded-xl border border-stone-100">
                  <div className="font-medium text-stone-700">Firma RGPD</div>
                  {isEditing ? (
                    <>
                      <select 
                        value={editForm.rgpd_status} 
                        onChange={e => setEditForm({...editForm, rgpd_status: e.target.value})}
                        className="mt-2 w-full bg-white border border-stone-200 rounded-md px-3 py-2 text-sm outline-none focus:border-amber-500"
                      >
                        <option value="Acepto">Acepto (Firmado)</option>
                        <option value="No acepto">No acepto</option>
                        <option value="Pendiente">Pendiente</option>
                      </select>
                      <div className="mt-3 flex items-center gap-2">
                        <input 
                          type="checkbox" 
                          id="edit_ads_consent"
                          checked={editForm.ads_consent} 
                          onChange={e => setEditForm({...editForm, ads_consent: e.target.checked})}
                          className="rounded border-stone-300 text-amber-500 focus:ring-amber-500"
                        />
                        <label htmlFor="edit_ads_consent" className="text-sm text-stone-700">Autoriza publicidad</label>
                      </div>
                    </>
                  ) : (
                    <div className="space-y-3">
                      <div className="text-sm mt-1">
                        {client.rgpd_status === 'Acepto'
                          ? <span className="text-teal-600 font-medium">Documento firmado electrónicamente.</span>
                          : <span className="text-rose-600 font-medium">Pendiente de firma.</span>}
                      </div>
                      
                      <div className="text-xs text-stone-500 flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${client.ads_consent ? 'bg-emerald-500' : 'bg-stone-300'}`}></div>
                        {client.ads_consent ? 'Autoriza publicidad (SMS/WhatsApp)' : 'No autoriza publicidad'}
                      </div>

                      {client.signature && (
                        <div className="mt-2 p-2 bg-white border border-stone-200 rounded-lg overflow-hidden">
                          <img 
                            src={client.signature} 
                            alt="Firma del cliente" 
                            className="max-h-32 mx-auto"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
                  <Clock size={18} className="text-stone-400" />
                  Observaciones
                </h3>
                {isEditing ? (
                  <textarea 
                    value={editForm.notes} 
                    onChange={e => setEditForm({...editForm, notes: e.target.value})}
                    className="w-full bg-amber-50/50 p-4 rounded-xl border border-amber-200 text-stone-700 min-h-[120px] outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="Añadir observaciones..."
                  />
                ) : (
                  <div className="bg-amber-50/50 p-4 rounded-xl border border-amber-100/50 text-stone-700 min-h-[120px] whitespace-pre-wrap">
                    {client.notes || 'No hay observaciones registradas para este cliente.'}
                  </div>
                )}
              </div>
              
              {isEditing && (
                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={() => setIsEditing(false)} className="px-4 py-2 text-stone-500 hover:bg-stone-100 rounded-lg transition-colors font-medium text-sm">
                    Cancelar
                  </button>
                  <button onClick={handleSaveEdit} className="flex items-center gap-2 bg-stone-800 hover:bg-stone-900 text-white px-5 py-2 rounded-lg font-medium text-sm transition-colors">
                    <Save size={16} />
                    Guardar Cambios
                  </button>
                </div>
              )}
              <div>
                <h3 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
                  <Activity size={18} className="text-stone-400" />
                  Historial Reciente
                </h3>

                <div className="space-y-6">
                  {/* Sesiones Historial */}
                  <div>
                    <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Últimas Sesiones</h4>
                    {loadingHistory ? (
                      <div className="text-sm text-stone-500">Cargando...</div>
                    ) : history.sessions.length > 0 ? (
                      <div className="bg-white border border-stone-100 rounded-xl overflow-hidden shadow-sm">
                        {history.sessions.slice((sessionPage - 1) * sessionsPerPage, sessionPage * sessionsPerPage).map((s, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 border-b border-stone-50 last:border-0 hover:bg-stone-50 transition group">
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-stone-800">{formatDate(s.date)} <span className="text-stone-400 font-normal ml-1">{s.time}</span></span>
                              {s.cabin && <span className="text-xs text-stone-500">Cabina: {s.cabin}</span>}
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-bold bg-amber-100 text-amber-800">
                                -{s.minutes} min
                              </span>
                              <button
                                onClick={() => handleDeleteSession(s.id)}
                                className="text-stone-300 hover:text-red-500 transition opacity-0 group-hover:opacity-100"
                                title="Eliminar sesión"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        ))}
                        
                        {history.sessions.length > sessionsPerPage && (
                          <div className="flex justify-between items-center px-3 py-2 bg-stone-50 border-t border-stone-100">
                            <button 
                              onClick={() => setSessionPage(p => Math.max(1, p - 1))}
                              disabled={sessionPage === 1}
                              className="text-[10px] font-bold text-stone-500 hover:text-stone-700 disabled:opacity-30 uppercase tracking-wider"
                            >
                              Anterior
                            </button>
                            <span className="text-[10px] text-stone-400 font-bold uppercase tracking-widest">Pág. {sessionPage} de {Math.ceil(history.sessions.length / sessionsPerPage)}</span>
                            <button 
                              onClick={() => setSessionPage(p => Math.min(Math.ceil(history.sessions.length / sessionsPerPage), p + 1))}
                              disabled={sessionPage >= Math.ceil(history.sessions.length / sessionsPerPage)}
                              className="text-[10px] font-bold text-stone-500 hover:text-stone-700 disabled:opacity-30 uppercase tracking-wider"
                            >
                              Siguiente
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-stone-50 p-4 rounded-xl border border-stone-100 text-sm text-stone-500 text-center">No hay sesiones.</div>
                    )}
                  </div>

                  {/* Bonos Historial */}
                  <div>
                    <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Bonos Comprados</h4>
                    {loadingHistory ? (
                      <div className="text-sm text-stone-500">Cargando...</div>
                    ) : history.vouchers.length > 0 ? (
                      <div className="bg-white border border-stone-100 rounded-xl overflow-hidden shadow-sm">
                        {history.vouchers.slice((voucherPage - 1) * sessionsPerPage, voucherPage * sessionsPerPage).map((v, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 border-b border-stone-50 last:border-0 hover:bg-stone-50 transition group">
                            <span className="text-sm font-medium text-stone-800">{formatDate(v.purchase_date)}</span>
                            <div className="flex items-center gap-3">
                              <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-bold bg-emerald-100 text-emerald-800">
                                +{v.minutes} min
                              </span>
                              <button
                                onClick={() => handleDeleteVoucher(v.id)}
                                className="text-stone-300 hover:text-red-500 transition opacity-0 group-hover:opacity-100"
                                title="Eliminar bono"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        ))}

                        {history.vouchers.length > sessionsPerPage && (
                          <div className="flex justify-between items-center px-3 py-2 bg-stone-50 border-t border-stone-100">
                            <button 
                              onClick={() => setVoucherPage(p => Math.max(1, p - 1))}
                              disabled={voucherPage === 1}
                              className="text-[10px] font-bold text-stone-500 hover:text-stone-700 disabled:opacity-30 uppercase tracking-wider"
                            >
                              Anterior
                            </button>
                            <span className="text-[10px] text-stone-400 font-bold uppercase tracking-widest">Pág. {voucherPage} de {Math.ceil(history.vouchers.length / sessionsPerPage)}</span>
                            <button 
                              onClick={() => setVoucherPage(p => Math.min(Math.ceil(history.vouchers.length / sessionsPerPage), p + 1))}
                              disabled={voucherPage >= Math.ceil(history.vouchers.length / sessionsPerPage)}
                              className="text-[10px] font-bold text-stone-500 hover:text-stone-700 disabled:opacity-30 uppercase tracking-wider"
                            >
                              Siguiente
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-stone-50 p-4 rounded-xl border border-stone-100 text-sm text-stone-500 text-center">No hay bonos.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Col - Actions */}
            <div className="space-y-8">

              {/* Registrar Sesión Card */}
              <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="bg-stone-50 px-5 py-4 border-b border-stone-100">
                  <h3 className="font-bold text-stone-800 flex items-center gap-2">
                    <Clock size={18} className="text-amber-500" />
                    Registrar Sesión Rápida
                  </h3>
                </div>
                <form onSubmit={handleCreateSession} className="p-5 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-stone-500 mb-1">Fecha</label>
                      <input type="date" required value={sessionForm.date} onChange={e => setSessionForm({ ...sessionForm, date: e.target.value })} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-stone-500 mb-1">Hora</label>
                      <input type="time" required value={sessionForm.time} onChange={e => setSessionForm({ ...sessionForm, time: e.target.value })} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-stone-500 mb-1">Minutos</label>
                      <input type="number" required min="1" value={sessionForm.minutes} onChange={e => setSessionForm({ ...sessionForm, minutes: parseInt(e.target.value) || 0 })} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-stone-500 mb-1">Cabina *</label>
                      <select 
                        value={sessionForm.cabin} 
                        onChange={e => setSessionForm({ ...sessionForm, cabin: e.target.value })} 
                        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                        required
                      >
                        <option value="" disabled>Seleccionar...</option>
                        {cabins.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <button type="submit" className="w-full bg-stone-800 hover:bg-stone-900 text-white font-medium py-2 rounded-lg text-sm transition-colors">
                    Descontar minutos y guardar
                  </button>
                </form>
              </div>

              {/* Vender Bono Card */}
              <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="bg-stone-50 px-5 py-4 border-b border-stone-100">
                  <h3 className="font-bold text-stone-800 flex items-center gap-2">
                    <Calendar size={18} className="text-emerald-500" />
                    Añadir Nuevo Bono
                  </h3>
                </div>
                <form onSubmit={handleCreateVoucher} className="p-5 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-stone-500 mb-1">Fecha</label>
                      <input type="date" required value={voucherForm.purchase_date} onChange={e => setVoucherForm({ ...voucherForm, purchase_date: e.target.value })} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-stone-500 mb-1">Tipo de Bono</label>
                      <select 
                        value={voucherForm.minutes} 
                        onChange={e => setVoucherForm({ ...voucherForm, minutes: parseInt(e.target.value) })} 
                        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                        required
                      >
                        <option value={80}>80 min</option>
                        <option value={176}>176 min</option>
                        <option value={272}>272 min</option>
                      </select>
                    </div>
                  </div>
                  <button type="submit" className="w-full bg-emerald-100 hover:bg-emerald-200 text-emerald-800 font-bold py-2 rounded-lg text-sm transition-colors">
                    Comprar bono y sumar minutos
                  </button>
                </form>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientProfileModal;
